import {
	Button,
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	Separator,
} from "@monbits/ui";
import { Loader2, X, XCircle } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

interface EntityDrawerProps {
	title: string;
	isLoading?: boolean;
	isNotFound?: boolean;
	notFoundMessage?: string;
	children: React.ReactNode;
	parentPath: string;
}

export function EntityDrawer({
	title,
	isLoading = false,
	isNotFound = false,
	notFoundMessage = "Not found",
	children,
	parentPath,
}: EntityDrawerProps) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [isOpen, setIsOpen] = React.useState(true);
	const closeTimeoutRef = React.useRef<number | null>(null);

	const handleClose = React.useCallback(() => {
		const queryString = searchParams.toString();
		const targetPath = queryString
			? `${parentPath}?${queryString}`
			: parentPath;
		navigate(targetPath);
	}, [navigate, parentPath, searchParams]);

	const requestClose = React.useCallback(() => {
		setIsOpen(false);
	}, []);

	React.useEffect(() => {
		if (isOpen) {
			return;
		}

		closeTimeoutRef.current = window.setTimeout(() => {
			handleClose();
		}, 200);

		return () => {
			if (closeTimeoutRef.current !== null) {
				window.clearTimeout(closeTimeoutRef.current);
				closeTimeoutRef.current = null;
			}
		};
	}, [handleClose, isOpen]);

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			if (!open) {
				requestClose();
			} else {
				setIsOpen(true);
			}
		},
		[requestClose],
	);

	return (
		<Drawer open={isOpen} onOpenChange={handleOpenChange} direction="right">
			<DrawerContent className="h-screen overflow-hidden flex flex-col w-full max-w-full sm:max-w-sm md:max-w-[600px]">
				<div className="flex flex-col h-full w-full">
					<DrawerHeader className="flex-shrink-0 relative">
						<DrawerTitle className="text-lg font-medium pr-8">
							{title}
						</DrawerTitle>
						<DrawerClose asChild>
							<Button
								variant="ghost"
								size="icon"
								className="absolute right-4 top-4 h-8 w-8 rounded-full"
								onClick={requestClose}
							>
								<X className="h-4 w-4" />
								<span className="sr-only">Close</span>
							</Button>
						</DrawerClose>
					</DrawerHeader>

					<Separator />

					<div className="flex-1 overflow-y-auto">
						{isLoading ? (
							<div className="flex flex-col items-center justify-center h-full p-6">
								<Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
								<p className="text-sm text-muted-foreground">Loading...</p>
							</div>
						) : isNotFound ? (
							<div className="flex flex-col items-center justify-center h-full p-6 text-center">
								<XCircle className="h-12 w-12 text-muted-foreground mb-4" />
								<h3 className="text-lg font-semibold mb-2">
									{notFoundMessage}
								</h3>
								<p className="text-sm text-muted-foreground mb-6">
									The item you are looking for could not be found.
								</p>
								<Button variant="outline" onClick={requestClose}>
									Go Back
								</Button>
							</div>
						) : (
							children
						)}
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
}
